import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  ButtonGroup,
} from "reactstrap";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import axiosInstance from "../../../helpers/axiosInstance";

const EcommerceBanners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [selectedType, setSelectedType] = useState("all");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    type: "home",
    images: [],
    isActive: true,
  });

  useEffect(() => {
    fetchBanners();
  }, [selectedType]);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/api/v1/admin/banners");
      const filteredBanners =
        selectedType === "all"
          ? response.data.banners
          : response.data.banners.filter(
              (banner) => banner.type === selectedType
            );
      setBanners(filteredBanners);
    } catch (error) {
      console.error("Error fetching banners:", error);
    } finally {
      setLoading(false);
    }
  };

  const uploadImageToOm = async (file) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      const response = await axiosInstance.post(
        "api/v1/upload-image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data.images;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  };
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    setUploadingImage(true);

    try {
      const uploadPromises = files.map(async (file) => {
        const result = await uploadImageToOm(file);
        console.log(result);
        return result;
      });
      console.log(uploadPromises);
      const uploadedUrls = await Promise.all(uploadPromises);
      console.log(uploadedUrls);

      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls],
      }));
    } catch (error) {
      console.error("Error uploading images:", error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post("/api/v1/admin/banners", formData);
      setModal(false);
      fetchBanners();
      setFormData({
        type: "home",
        images: [],
        isActive: true,
      });
    } catch (error) {
      console.error("Error creating banner:", error);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await axiosInstance.put(`/api/v1/admin/banner/${id}/toggle`);
      fetchBanners();
    } catch (error) {
      console.error("Error toggling banner status:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this banner?")) {
      try {
        await axiosInstance.delete(`/api/v1/admin/banner/${id}`);
        fetchBanners();
      } catch (error) {
        console.error("Error deleting banner:", error);
      }
    }
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Banners"
          breadcrumbItems={[
            { title: "Ecommerce", link: "#" },
            { title: "Banners", link: "#" },
          ]}
        />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h4 className="card-title mb-0">Banners List</h4>
                  </div>
                  <div className="d-flex gap-2">
                    <ButtonGroup>
                      <Button
                        color={selectedType === "all" ? "primary" : "light"}
                        onClick={() => setSelectedType("all")}
                      >
                        All
                      </Button>
                      <Button
                        color={selectedType === "home" ? "primary" : "light"}
                        onClick={() => setSelectedType("home")}
                      >
                        Home
                      </Button>
                      <Button
                        color={
                          selectedType === "category" ? "primary" : "light"
                        }
                        onClick={() => setSelectedType("category")}
                      >
                        Category
                      </Button>
                      <Button
                        color={selectedType === "product" ? "primary" : "light"}
                        onClick={() => setSelectedType("product")}
                      >
                        Product
                      </Button>
                      <Button
                        color={selectedType === "offer" ? "primary" : "light"}
                        onClick={() => setSelectedType("offer")}
                      >
                        Offer
                      </Button>
                    </ButtonGroup>
                    <Button color="primary" onClick={() => setModal(true)}>
                      Add New Banner
                    </Button>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center my-5">
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-centered table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Type</th>
                          <th>Images</th>
                          <th>Status</th>
                          <th>Created At</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {banners.map((banner) => (
                          <tr key={banner._id}>
                            <td>
                              <Badge color="info" className="text-uppercase">
                                {banner.type}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                {banner.images.map((image, index) => (
                                  <img
                                    key={index}
                                    src={image}
                                    alt={`Banner ${index + 1}`}
                                    className="rounded"
                                    style={{
                                      width: "50px",
                                      height: "50px",
                                      objectFit: "cover",
                                    }}
                                  />
                                ))}
                              </div>
                            </td>
                            <td>
                              <Badge
                                color={banner.isActive ? "success" : "danger"}
                                role="button"
                                onClick={() => handleToggleStatus(banner._id)}
                              >
                                {banner.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </td>
                            <td>
                              {new Date(banner.createdAt).toLocaleDateString(
                                "en-IN",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                }
                              )}
                            </td>
                            <td>
                              <div className="d-flex gap-3">
                                <Link
                                  to="#"
                                  className="text-danger"
                                  onClick={() => handleDelete(banner._id)}
                                >
                                  <i className="mdi mdi-delete font-size-18" />
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Add Banner Modal */}
        <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
          <ModalHeader toggle={() => setModal(false)}>
            Add New Banner
          </ModalHeader>
          <ModalBody>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label for="type">Banner Type</Label>
                <Input
                  type="select"
                  id="type"
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  required
                >
                  <option value="home">Home</option>
                  <option value="category">Category</option>
                  <option value="product">Product</option>
                  <option value="offer">Offer</option>
                </Input>
              </FormGroup>

              <FormGroup>
                <Label>Images</Label>
                <div className="mb-3">
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                  />
                </div>
                {uploadingImage && (
                  <div className="text-center mb-3">
                    <Spinner size="sm" /> Uploading...
                  </div>
                )}
                {formData.images.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {formData.images.map((image, index) => (
                      <div key={index} className="position-relative">
                        <img
                          src={image}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                          }}
                          className="rounded"
                        />
                        <Button
                          color="danger"
                          size="sm"
                          className="position-absolute top-0 end-0"
                          onClick={() => removeImage(index)}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <div className="form-check">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                  <Label className="form-check-label" for="isActive">
                    Active
                  </Label>
                </div>
              </FormGroup>

              <div className="text-end mt-4">
                <Button
                  type="button"
                  color="light"
                  className="me-2"
                  onClick={() => setModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={uploadingImage || formData.images.length === 0}
                >
                  Create Banner
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default EcommerceBanners;
